<template>
  <sl-tooltip ref="tooltipViewDetails" class="inverted" style="--max-width: 335px;" trigger="manual" hoist placement="right">
    <div slot="content" class="tooltip-content">
      <div class="tooltip-header">
        <span class="view-name">{{ changedView.name }}</span>
        <span class="information-message">
          {{ changedView.isDefault ? "Click to clear view & show all data" : "Click to apply this view to the current page" }}
        </span>
      </div>
      <div class="tooltip-body">
        <filter-chips-item v-for="item in changedView.chips" :key="item.property" :item="item" non-removable/>
      </div>
    </div>
    <div 
      :id="getViewHtmlElementId(changedView.id)"
      :class="[
      'filter-view-item', 
      { 'active': isSelected }, 
      { 'view-menu-open': isButtonActionMenuOpen },
      { 'no-hover': isHoverStyleDisabled && !isButtonActionMenuOpen },
      { 'draggable': draggable && isViewDragging }]"
      :draggable="draggable"
      @click.exact="handleViewItemClick"
      @click.ctrl.exact="handleViewItemCtrlClick"
      @click.right.prevent="handleViewItemClickRight"
      @mousemove="handleViewItemMouseMove" 
      @mouseleave="handleViewItemMouseLeave"
      @dragstart.stop="handleViewItemDragStart"
      @dragend.stop="handleViewItemDragEnd"
      @dragover.prevent
      @dragenter.prevent
      @dragleave.prevent
    >
      <span :class="isSelected ? 'icon-diamond' : 'icon-diamond-outline'"></span>
      <div class="item-name">{{ changedView.name }}</div>
      <mc-menu v-if="changedView && !changedView.isDefault" ref="buttonActionMenu" 
        :items="buttonActionMenuItems"
        :placement="actionMenuPlacement"
        @mousemove:buttonAction="handleButtonActionMouseMove"
        @click:item="handleViewMenuItemClick($event)"
        @click.right.stop
        @click.stop
        @close="handleViewMenuClose">
      </mc-menu>
    </div>
  </sl-tooltip>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, watch } from 'vue';
import _ from 'lodash';
import FilterChipsItem from '@/components/shared/filters/FilterChips/FilterChipsItem.vue';
import McMenu from '@/components/ui/McMenu/McMenu.vue';
import { useQuickFilterStore } from '@/store/quickFilterStore';
import { MENU_CLOSE_DELAY, ACTION_MENU_PLACEMENT_DEFAULT, ACTION_MENU_PLACEMENT_RIGHT_CLICK } from '@/constants/menu';
import { TOOLTIP_DISPLAY_DELAY } from '@/constants/tooltip';
import { getViewHtmlElementId } from '@/helpers/filterView';
import { FILTER_VIEW_MENU_ITEMS } from '@/constants/quickFilter';
import { IMenuItemEvent } from '@/models/menu.model';
import { FilterViewItemType, FilterViewItemActionType, IQuickFilterViewModel } from '@/models/quickFilter.model';

interface IFilterViewItemProps {
  view: IQuickFilterViewModel;
  isSelected?: boolean;
  draggable?: boolean;
}

const props = withDefaults(defineProps<IFilterViewItemProps>(), {
  isSelected: false,
  draggable: false,
});

const { view, isSelected } = toRefs(props);

const quickFilterStore = useQuickFilterStore();

const changedView = ref(_.cloneDeep(view.value));
const availableFolders = computed(() => quickFilterStore.quickFilterFolders.filter((folder) => folder.id !== changedView.value.folderId));

// Used when displaying tooltips.
// Since the tooltip opens with a delay, it is necessary to know whether the course is in the view item element area
const isCursorOverViewItem = ref(false);
// This parameter is used to disable the hover style when there is an item with an open menu in the list
const isHoverStyleDisabled = ref(false);
const isViewDragging = ref(false);

const tooltipViewDetails = ref();
const buttonActionMenu = ref();
const actionMenuPlacement = ref(_.cloneDeep(ACTION_MENU_PLACEMENT_DEFAULT));
const isButtonActionMenuOpen = computed(() => buttonActionMenu.value?.isMenuOpen);
const buttonActionMenuItems = computed(() => {
  return FILTER_VIEW_MENU_ITEMS.map((item) => {
    if (item.value === FilterViewItemActionType.removeFromFolder) {
      return {
        ...item,
        disabled: changedView.value.folderId ? false : true,
        tooltip: changedView.value.folderId ? item.tooltip : "This View is not in a Folder",
      };
    }
    else if (item.value === FilterViewItemActionType.move) {
      return {
        ...item,
        disabled: availableFolders.value.length === 0,
        tooltip: availableFolders.value.length === 0 ? "No Folders available" : item.tooltip,
        children: availableFolders.value.map((folder) => {
          return {
            label: folder.name,
            value: folder.id,
            icon: "icon-folder-outline",
          };
        })
      };
    }

    return { ...item };
  });
});

const emit = defineEmits([
  'click:apply',
  'click:menuItem'
]);

const handleViewItemClick = () => {
  emit('click:apply', changedView.value);
};

const handleViewItemCtrlClick = () => {
  handleViewMenuItemClick({ value: FilterViewItemActionType.openInNewTab } as IMenuItemEvent);
};

const handleViewMenuItemClick = (event: IMenuItemEvent) => {
  const actionType = event.value as FilterViewItemActionType;
  if (!actionType) return;

  if (actionType === FilterViewItemActionType.removeFromFolder) {
    changedView.value.folderId = undefined;
  }
  else if (actionType === FilterViewItemActionType.move) {
    if (!event.childValue?.value) return;
    changedView.value.folderId = event.childValue?.value as string;
  }

  const eventData = {
    data: changedView.value,
    actionItemType: FilterViewItemType.view,
    actionType: actionType,
  }

  emit('click:menuItem', eventData);
};

const handleViewItemMouseMove = () => {
  setMouseMoveAttributes(true);

  setTimeout(
    () => {
      if (isCursorOverViewItem.value
        && !tooltipViewDetails.value?.open
        && !buttonActionMenu.value?.tooltipButtonAction.open
        && !isButtonActionMenuOpen.value
        && !isHoverStyleDisabled.value
      ) {
        tooltipViewDetails.value?.show();
      }
    },
    TOOLTIP_DISPLAY_DELAY
  );
};

const handleViewItemMouseLeave = () => {
  setMouseMoveAttributes();
  tooltipViewDetails.value?.hide();

  setTimeout(
    () => {
      if (!isCursorOverViewItem.value && isButtonActionMenuOpen.value) {
        buttonActionMenu.value?.closeMenu();
      }
    },
    MENU_CLOSE_DELAY
  );
};

const handleButtonActionMouseMove = () => {
  if (tooltipViewDetails.value?.open) {
    tooltipViewDetails.value?.hide();
  }
};

const handleViewItemClickRight = () => {
  if (isButtonActionMenuOpen.value) {
    buttonActionMenu.value?.closeMenu();
  }
  else {
    buttonActionMenu.value?.openMenu();
    actionMenuPlacement.value = _.cloneDeep(ACTION_MENU_PLACEMENT_RIGHT_CLICK);
  }

  if (tooltipViewDetails.value?.open) {
    tooltipViewDetails.value?.hide();
  }
};

const handleViewItemDragStart = (event: DragEvent) => {
  isViewDragging.value = true;

  if (!event || !event.dataTransfer) return;

  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.dropEffect = 'move';
  event.dataTransfer.setData('text/plain', JSON.stringify(changedView.value));

  setMouseMoveAttributes();
};

const handleViewItemDragEnd = () => {
  isViewDragging.value = false;
  setMouseMoveAttributes();
};

const handleViewMenuClose = () => {
  actionMenuPlacement.value = _.cloneDeep(ACTION_MENU_PLACEMENT_DEFAULT);
  setMouseMoveAttributes()
};

const setToDefaultState = () => {
  changedView.value = _.cloneDeep(view.value);
  setMouseMoveAttributes()
};

const setMouseMoveAttributes = (isCursorOverElement: boolean = false) => {
  isCursorOverViewItem.value = isCursorOverElement;
  isHoverStyleDisabled.value = document.querySelectorAll('div.view-menu-open').length > 0 || document.querySelectorAll('div.draggable').length > 0;
};

watch(
  () => view.value,
  () => setToDefaultState(),
  { deep: true }
);
</script>

<style lang="scss" scoped>
.filter-view-item {
  --mc-menu-width: 186px;

  display: flex;
  align-items: center;
  align-self: stretch;
  flex-shrink: 0;
  height: 33px;
  padding: 4px 4px 4px 8px;
  gap: 8px;
  cursor: pointer;

  .icon-diamond,
  .icon-diamond-outline {
    font-size: 12px;
    color: var(--base-bright-pink);
  }

  .icon-diamond {
    font-size: 14px;
  }

  .item-name {
    font-size: var(--font-size-headers-x-small);
    color: var(--colour-text-neutral-default-dark);
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    text-wrap: nowrap;
    user-select: none;
  }

  :deep(.button-action) {
    display: none;
  }

  &.view-menu-open,
  &:hover:not(.no-hover) {
    :deep(.button-action) {
      display: flex;
    }
  }

  &.draggable,
  &:hover:not(.no-hover) {
    border-radius: 8px;
    background-color: var(--colour-background-neutral-hover-a-dark);
  }

  &.active,
  &.view-menu-open {
    border-radius: 8px;
    background-color: var(--colour-background-neutral-default-a-dark);
  }

  &.draggable {
    border: 2px dashed var(--colour-icons-disabled-dark);
  }
}

.tooltip-content {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .tooltip-header {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .view-name {
      color: var(--colour-text-neutral-default);
      font-size: var(--font-size-headers-x-small);
      font-weight: var(--font-weight-body-medium);
      line-height: var(--line-height-s);
      word-wrap: break-word;
    }

    .information-message {
      color: var(--colour-text-neutral-subtle);
      font-size: var(--font-size-body-small);
      font-style: italic;
      line-height: var(--line-height-s);
    }
  }

  .tooltip-body {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>